// src/pages/msp-inventory/pages/reports/RestockAlertsPage.jsx
import React, { useEffect, useState } from "react";
import { AlertTriangle, X, Pencil, Check, Plus } from "lucide-react";
import ExportCsvButton from "../../components/reports/ExportCsvButton";
import { exportSkuReportCsv } from "../../utils/exportCsv";
import ClickableSkuCell from "../../components/reports/ClickableSkuCell";
import { SortableTH, Pagination } from "../../components/reports/TableControls";
import { getRestockAlerts } from "../../services/catalogService";
import { useImageBackfill } from "../../hooks/useImageBackfill";
import { useSortAndPaginate } from "../../hooks/useSortAndPaginate";
import { usePersistedState } from "../../hooks/usePersistedState";
import RefreshableSection from "../../components/reports/RefreshableSection";
import { buildCacheKey, getCachedReport, setCachedReport, invalidateCachedReport } from "../../utils/reportCache";
import RefreshReportBar from "../../components/reports/RefreshReportBar";
import LoadingGame from "../../components/reports/LoadingGame";

const HIDDEN_ALERTS_KEY = "reports:restock:hiddenItemIds";

const loadHiddenIds = () => {
    try {
        const raw = localStorage.getItem(HIDDEN_ALERTS_KEY);
        return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
        return new Set();
    }
};

// Units still needed to cover `targetDays` of sales at the item's current
// velocity, netting out what's already on hand plus anything inbound.
const suggestedReorderQty = (alert, targetDays) => {
    if (!alert.velocity_per_day || alert.velocity_per_day <= 0) return 0;
    const needed = Math.ceil(alert.velocity_per_day * targetDays);
    const onHandOrComing = (alert.stock || 0) + (alert.inbound_qty || 0);
    return Math.max(0, needed - onHandOrComing);
};

// Every numeric column a seller might want to build an ad-hoc filter on.
// Each entry maps a stable field id -> a getter off the (already-enriched
// with suggested_reorder_qty) alert row, so "filter by any param" doesn't
// need a hardcoded filter per column — one generic row UI handles all of them.
const FILTERABLE_FIELDS = [
    { id: "units_30d", label: "Sold (30d)", get: (a) => a.units_30d },
    { id: "velocity_per_day", label: "Velocity/day", get: (a) => a.velocity_per_day },
    { id: "stock", label: "Current Stock", get: (a) => a.stock },
    { id: "inbound_qty", label: "Inbound Qty", get: (a) => a.inbound_qty },
    { id: "days_of_stock_remaining", label: "Days Left", get: (a) => a.days_of_stock_remaining },
    { id: "unit_cost", label: "Unit Cost ($)", get: (a) => a.unit_cost },
    { id: "unit_price", label: "Unit Price ($)", get: (a) => a.unit_price },
    { id: "unit_fees", label: "Unit Fees ($)", get: (a) => a.unit_fees },
    { id: "estimated_profit_per_unit", label: "Profit/Unit ($)", get: (a) => a.estimated_profit_per_unit },
    { id: "estimated_roi", label: "ROI %", get: (a) => a.estimated_roi },
    { id: "suggested_reorder_qty", label: "Reorder Qty", get: (a) => a.suggested_reorder_qty },
];
const fieldById = (id) => FILTERABLE_FIELDS.find((f) => f.id === id);

const emptyCustomFilter = () => ({ field: "velocity_per_day", op: "min", value: "" });

const RestockAlertsPage = ({ onOpenSku }) => {
    const [filters, setFilters] = usePersistedState("reports:restock:filters", {
        threshold: 14, statusFilter: "", targetDays: 30, hideNoRecentOrders: true,
        leadTimeDays: 0, showOnlyLeadTimeUrgent: false,
        customFilters: [], // [{ field, op: 'min'|'max', value }]
    });
    const { threshold, statusFilter, targetDays, hideNoRecentOrders, leadTimeDays, showOnlyLeadTimeUrgent } = filters;
    const customFilters = filters.customFilters || [];
    const setThreshold = (v) => setFilters((f) => ({ ...f, threshold: v }));
    const setStatusFilter = (v) => setFilters((f) => ({ ...f, statusFilter: v }));
    const setTargetDays = (v) => setFilters((f) => ({ ...f, targetDays: v }));
    const setHideNoRecentOrders = (v) => setFilters((f) => ({ ...f, hideNoRecentOrders: v }));
    const setLeadTimeDays = (v) => setFilters((f) => ({ ...f, leadTimeDays: v }));
    const setShowOnlyLeadTimeUrgent = (v) => setFilters((f) => ({ ...f, showOnlyLeadTimeUrgent: v }));
    const setCustomFilters = (updater) => setFilters((f) => ({
        ...f, customFilters: typeof updater === "function" ? updater(f.customFilters || []) : updater,
    }));

    const addCustomFilter = () => setCustomFilters((cf) => [...cf, emptyCustomFilter()]);
    const removeCustomFilter = (idx) => setCustomFilters((cf) => cf.filter((_, i) => i !== idx));
    const updateCustomFilter = (idx, patch) => setCustomFilters((cf) =>
        cf.map((f, i) => (i === idx ? { ...f, ...patch } : f)));

    // Inline pencil-edit on the Reorder column header.
    const [editingTargetDays, setEditingTargetDays] = useState(false);
    const [targetDaysDraft, setTargetDaysDraft] = useState(String(targetDays));
    const saveTargetDays = () => {
        const n = Math.max(1, Number(targetDaysDraft) || 1);
        setTargetDays(n);
        setEditingTargetDays(false);
    };

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [hiddenIds, setHiddenIds] = useState(loadHiddenIds);
    const [savedAt, setSavedAt] = useState(null);

    const hideItem = (id) => setHiddenIds((prev) => {
        const next = new Set(prev);
        next.add(id);
        localStorage.setItem(HIDDEN_ALERTS_KEY, JSON.stringify([...next]));
        return next;
    });
    const unhideAll = () => {
        setHiddenIds(new Set());
        localStorage.removeItem(HIDDEN_ALERTS_KEY);
    };

    const cacheKey = buildCacheKey("restock", { threshold });

    const loadReport = async (forceRefresh = false) => {
        const isInitialLoad = !data;

        if (!forceRefresh) {
            const cached = await getCachedReport(cacheKey);
            if (cached) {
                setData(cached.data);
                setSavedAt(cached.savedAt);
                setLoading(false);
                return;
            }
        }

        if (isInitialLoad) setLoading(true);
        else setRefreshing(true);
        try {
            const d = await getRestockAlerts(threshold);
            setData(d);
            setSavedAt(Date.now());
            setCachedReport(cacheKey, d);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        loadReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [threshold]);

    const handleForceRefresh = async () => {
        await invalidateCachedReport(cacheKey);
        await loadReport(true);
    };

    const patchAlert = (id, patch) => {
        setData((prev) => prev ? {
            ...prev,
            alerts: prev.alerts.map((a) => a.catalog_item_id === id ? { ...a, ...patch } : a),
        } : prev);
    };

    const availableStatuses = [...new Set((data?.alerts || []).map((a) => a.published_status).filter(Boolean))];
    const targetDaysNum = Number(targetDays) || 30;
    const leadTimeDaysNum = Number(leadTimeDays) || 0;

    // Enrich first (adds suggested_reorder_qty and needs_restock_now, needed
    // since they're themselves filterable/sortable), THEN apply every active
    // filter — built-in ones plus every custom rule the seller has added.
    const enrichedAlerts = (data?.alerts || []).map((a) => ({
        ...a,
        suggested_reorder_qty: suggestedReorderQty(a, targetDaysNum),
        needs_restock_now: leadTimeDaysNum > 0 && (a.is_out_of_stock || a.days_of_stock_remaining <= leadTimeDaysNum),
    }));

    const activeCustomFilters = customFilters.filter((cf) => cf.value !== "" && cf.value != null);

    const visibleAlerts = enrichedAlerts
        .filter((a) => !hiddenIds.has(a.catalog_item_id))
        .filter((a) => !statusFilter || a.published_status === statusFilter)
        .filter((a) => !hideNoRecentOrders || (a.units_30d || 0) > 0)
        .filter((a) => !showOnlyLeadTimeUrgent || a.needs_restock_now)
        .filter((a) => activeCustomFilters.every((cf) => {
            const field = fieldById(cf.field);
            if (!field) return true;
            const val = field.get(a);
            const threshold_ = Number(cf.value);
            if (val == null || isNaN(threshold_)) return true; // unknown value never fails a filter
            return cf.op === "min" ? val >= threshold_ : val <= threshold_;
        }));

    const hiddenCount = (data?.alerts || []).filter((a) => hiddenIds.has(a.catalog_item_id)).length;
    const anyBuiltInFilterActive = !!statusFilter || showOnlyLeadTimeUrgent;
    const anyFilterActive = anyBuiltInFilterActive || activeCustomFilters.length > 0;

    const { sorted, pageItems, page, setPage, totalPages, sortConfig, requestSort, pageSize } =
        useSortAndPaginate(visibleAlerts, { key: "days_of_stock_remaining", dir: "asc" }, "reports:restock:table");

    useImageBackfill(sorted, patchAlert);

    const severity = (alert) => {
        if (alert.is_out_of_stock) return "red";
        const days = alert.days_of_stock_remaining;
        return days <= 3 ? "red" : days <= 7 ? "orange" : "yellow";
    };
    const severityClasses = { red: "bg-red-50 text-red-600", orange: "bg-orange-50 text-orange-600", yellow: "bg-yellow-50 text-yellow-700" };

    const statusClasses = (status) =>
        status === "PUBLISHED" ? "bg-green-50 text-green-600" : "bg-orange-50 text-orange-600";

    const roiClasses = (roi) => roi == null ? "text-gray-300" : roi >= 0 ? "text-purple-600" : "text-red-600";
    const profitClasses = (profit) => profit >= 0 ? "text-green-600" : "text-red-600";

    return (
        <div className="max-w-6xl mx-auto py-8 px-6">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <AlertTriangle size={22} className="text-orange-500" /> Restock Alerts
                    </h1>
                    <p className="text-gray-500 text-sm">Fast-selling (and out-of-stock) items, with profitability and suggested reorder quantities</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <RefreshReportBar savedAt={savedAt} refreshing={refreshing} onRefresh={handleForceRefresh} onAutoRefresh={handleForceRefresh} />
                    {hiddenCount > 0 && (
                        <button onClick={unhideAll} className="text-xs text-teal-600 hover:text-teal-700 font-semibold underline decoration-dotted">
                            Unhide {hiddenCount} hidden item{hiddenCount === 1 ? "" : "s"}
                        </button>
                    )}
                    <ExportCsvButton onExport={() => exportSkuReportCsv({
                        filename: `restock_alerts_${new Date().toISOString().slice(0, 10)}.csv`,
                        rows: sorted,
                        idKey: "catalog_item_id",
                        headers: [
                            "Item", "SKU", "Status", "Current Stock", "Inbound", "Sold (30d)", "Velocity/day",
                            "Days Left", "Days OOS", "Unit Cost", "Unit Fees", "Profit/Unit", "ROI %",
                            `Reorder Qty (${targetDaysNum}d)`,
                        ],
                        getRowValues: (r) => [
                            r.title, r.sku, r.published_status, r.stock, r.inbound_qty, r.units_30d, r.velocity_per_day,
                            r.days_of_stock_remaining, r.days_out_of_stock ?? "", r.unit_cost, r.unit_fees,
                            r.estimated_profit_per_unit, r.estimated_roi ?? "", r.suggested_reorder_qty,
                        ],
                    })} />
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-4 mb-4">
                <div className="flex items-center gap-3 flex-wrap mb-3">
                    <div className="flex items-center gap-2">
                        <label className="text-xs text-gray-500 font-semibold">Alert threshold:</label>
                        <select value={threshold} onChange={(e) => setThreshold(Number(e.target.value))}
                            className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500">
                            <option value={7}>7 days</option>
                            <option value={14}>14 days</option>
                            <option value={30}>30 days</option>
                        </select>
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-xs text-gray-500 font-semibold">Lead time (days):</label>
                        <input
                            type="number" min="0" value={leadTimeDays}
                            onChange={(e) => setLeadTimeDays(e.target.value)}
                            title="How many days it takes for a restock to ship, check in, and become sellable"
                            className="w-16 border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-xs text-gray-500 font-semibold">Status:</label>
                        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                            className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500">
                            <option value="">All statuses</option>
                            {availableStatuses.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                    <label className="flex items-center gap-1.5 text-xs text-gray-500 font-semibold cursor-pointer">
                        <input
                            type="checkbox" checked={hideNoRecentOrders}
                            onChange={(e) => setHideNoRecentOrders(e.target.checked)}
                            className="accent-teal-600"
                        />
                        Hide items with no orders in the last 30 days
                    </label>
                    <label className="flex items-center gap-1.5 text-xs text-gray-500 font-semibold cursor-pointer">
                        <input
                            type="checkbox" checked={showOnlyLeadTimeUrgent}
                            onChange={(e) => setShowOnlyLeadTimeUrgent(e.target.checked)}
                            className="accent-teal-600"
                        />
                        Only show items needing restock within lead time
                    </label>
                    {anyFilterActive && (
                        <button
                            onClick={() => { setStatusFilter(""); setCustomFilters([]); setShowOnlyLeadTimeUrgent(false); }}
                            className="text-xs text-gray-400 hover:text-gray-600 font-semibold underline decoration-dotted"
                        >
                            Clear filters
                        </button>
                    )}
                </div>

                {/* Generic "filter by any param" rows — add as many as needed,
                    each picking any numeric column + min/max + a value. */}
                <div className="pt-3 border-t border-gray-100 space-y-2">
                    {customFilters.map((cf, idx) => (
                        <div key={idx} className="flex items-center gap-2 flex-wrap">
                            <select
                                value={cf.field}
                                onChange={(e) => updateCustomFilter(idx, { field: e.target.value })}
                                className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500"
                            >
                                {FILTERABLE_FIELDS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                            </select>
                            <select
                                value={cf.op}
                                onChange={(e) => updateCustomFilter(idx, { op: e.target.value })}
                                className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500"
                            >
                                <option value="min">≥ (min)</option>
                                <option value="max">≤ (max)</option>
                            </select>
                            <input
                                type="number" step="any" placeholder="Value" value={cf.value}
                                onChange={(e) => updateCustomFilter(idx, { value: e.target.value })}
                                className="w-24 border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-teal-500"
                            />
                            <button onClick={() => removeCustomFilter(idx)} className="text-gray-300 hover:text-red-500">
                                <X size={14} />
                            </button>
                        </div>
                    ))}
                    <button
                        onClick={addCustomFilter}
                        className="flex items-center gap-1 text-xs font-bold text-teal-600 hover:text-teal-700"
                    >
                        <Plus size={12} /> Add Filter
                    </button>
                </div>
            </div>

            {loading ? (
                <LoadingGame label="Loading restock alerts..." />
            ) : (
                <RefreshableSection refreshing={refreshing}>
                    <div className="bg-white rounded-2xl shadow-lg p-5 overflow-x-auto max-h-[70vh] overflow-y-auto">
                        <table className="w-full text-xs">
                            <thead className="sticky top-0 z-10 bg-white">
                                <tr className="text-left text-gray-400 border-b border-gray-100">
                                    <SortableTH label="Item" sortKey="title" sortConfig={sortConfig} onSort={requestSort} />
                                    <th className="pb-2 font-semibold whitespace-nowrap">Walmart</th>
                                    <SortableTH label="Status" sortKey="published_status" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableTH label="Current Stock" sortKey="stock" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableTH label="Inbound" sortKey="inbound_qty" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableTH label="Sold (30d)" sortKey="units_30d" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableTH label="Velocity /day" sortKey="velocity_per_day" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableTH label="Days Left" sortKey="days_of_stock_remaining" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableTH label="Profit/Unit" sortKey="estimated_profit_per_unit" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableTH label="ROI %" sortKey="estimated_roi" sortConfig={sortConfig} onSort={requestSort} />
                                    <th className="pb-2 font-semibold whitespace-nowrap">
                                        <div className="relative inline-flex items-center gap-1">
                                            <button onClick={() => requestSort("suggested_reorder_qty")} className="hover:text-gray-600">
                                                Reorder ({targetDaysNum}d)
                                                {sortConfig.key === "suggested_reorder_qty" && (sortConfig.dir === "asc" ? " ▲" : " ▼")}
                                            </button>
                                            <button
                                                onClick={() => { setTargetDaysDraft(String(targetDays)); setEditingTargetDays((o) => !o); }}
                                                title="Change how many days of sales to reorder for"
                                                className="flex items-center justify-center w-4 h-4 rounded-full bg-teal-100 text-teal-600 hover:bg-teal-600 hover:text-white transition flex-shrink-0"
                                            >
                                                <Pencil size={9} />
                                            </button>
                                            {editingTargetDays && (
                                                <div className="absolute z-20 top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-2.5 w-44 normal-case font-normal">
                                                    <p className="text-[9px] font-bold text-gray-400 uppercase mb-1.5">Days of sales to cover</p>
                                                    <div className="flex items-center gap-1.5">
                                                        <input
                                                            type="number" min="1" autoFocus value={targetDaysDraft}
                                                            onChange={(e) => setTargetDaysDraft(e.target.value)}
                                                            onKeyDown={(e) => e.key === "Enter" && saveTargetDays()}
                                                            className="w-16 border border-gray-200 rounded-lg px-2 py-1 text-xs text-center focus:outline-none focus:border-teal-500"
                                                        />
                                                        <button onClick={saveTargetDays} className="text-teal-600 hover:text-teal-700">
                                                            <Check size={14} />
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </th>
                                    <th className="w-8"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {pageItems.map((a) => (
                                    <tr key={a.catalog_item_id} className="border-b border-gray-50 group">
                                        <td className="py-2"><ClickableSkuCell item={a} onOpen={onOpenSku} /></td>
                                        <td className="py-2 pl-2 pr-4">
                                            {a.wpid ? (
                                                <a
                                                    href={`https://www.walmart.com/ip/${a.wpid}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-teal-600 hover:text-teal-700 font-semibold underline decoration-dotted"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    OPEN
                                                </a>
                                            ) : (
                                                <span className="text-gray-300">—</span>
                                            )}
                                        </td>
                                        <td className="py-2">
                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${statusClasses(a.published_status)}`}>
                                                {a.published_status || "—"}
                                            </span>
                                        </td>
                                        <td className="py-2">{a.stock}</td>
                                        <td className="py-2">
                                            {a.inbound_qty > 0 ? <span className="text-blue-600 font-semibold">+{a.inbound_qty}</span> : <span className="text-gray-300">—</span>}
                                        </td>
                                        <td className="py-2">{a.units_30d}</td>
                                        <td className="py-2">{a.velocity_per_day}</td>
                                        <td className="py-2">
                                            <span className={`px-2 py-0.5 rounded-full font-bold whitespace-nowrap ${severityClasses[severity(a)]}`}>
                                                {a.is_out_of_stock
                                                    ? (a.days_out_of_stock != null
                                                        ? `OOS ${a.days_out_of_stock_is_estimate ? "~" : ""}${a.days_out_of_stock}d ago`
                                                        : "OOS")
                                                    : `${a.days_of_stock_remaining}d`}
                                            </span>
                                            {a.needs_restock_now && !a.is_out_of_stock && (
                                                <span title={`Within your ${leadTimeDaysNum}-day lead time — order now or it'll be OOS before restock arrives`}
                                                    className="ml-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 whitespace-nowrap">
                                                    ORDER NOW
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-2">
                                            <span className={`font-semibold ${profitClasses(a.estimated_profit_per_unit)}`}>
                                                ${a.estimated_profit_per_unit?.toFixed(2)}
                                            </span>
                                        </td>
                                        <td className="py-2">
                                            <span className={`font-semibold ${roiClasses(a.estimated_roi)}`}>
                                                {a.estimated_roi != null ? `${a.estimated_roi}%` : "—"}
                                            </span>
                                        </td>
                                        <td className="py-2 text-center">
                                            {a.suggested_reorder_qty > 0
                                                ? <span className="font-bold text-teal-700">{a.suggested_reorder_qty}</span>
                                                : <span className="text-gray-300">—</span>}
                                        </td>
                                        <td className="py-2">
                                            <button onClick={() => hideItem(a.catalog_item_id)} title="Hide this alert"
                                                className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition">
                                                <X size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {pageItems.length === 0 && (
                                    <tr><td colSpan={11} className="py-8 text-center text-gray-400">
                                        {hiddenCount > 0 || anyFilterActive ? "No alerts match your current filters." : "No items below the threshold — you're stocked up 🎉"}
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                        <Pagination page={page} totalPages={totalPages} setPage={setPage} totalItems={sorted.length} pageSize={pageSize} />
                    </div>
                </RefreshableSection>
            )}
        </div>
    );
};

export default RestockAlertsPage;